import React from 'react'
import axios from 'axios'
import { SubmitHandler, useForm } from 'react-hook-form'
import { RouteComponentProps } from 'react-router-dom'
import { LoginButtonBar, LoginPageLayout, SsoButton } from './LoginPageLayout'
import { useWistorAuth } from '../../api/auth'
import { AppLoginStatus, useAppLoginStore, AppLoginCredentials, appLogin } from '../../api/app-login'
import { SingleLoginView } from '../SingleLogin'

type SsoConfig = {
  uri: string
  label: string
  comment: string
  ssoClientId: string
  repositories: string[]
  enabled: boolean
  fullAccess: boolean
  image: string
}

/**
 * TODO: figure out what to do when single login user does not have rights later
 */
export function RepoLogin(props: RouteComponentProps) {
  const {
    register,
    handleSubmit,
    formState
  } = useForm<AppLoginCredentials>()
  const [ errorReason, setErrorReason ] = React.useState<string | null>(null)
  const [ ssoConfigs, setSsoConfigs ] = React.useState<SsoConfig[]>([])
  const appLoginStatus = useAppLoginStore(st => st.status)
  const auth = useWistorAuth()

  const repoInfo = getRepoInfoFromSearchParameters()
  React.useEffect(() => {
    if (!repoInfo?.repo) return
    fetchSsoConfigs()
   .then(ssoConfigs => setSsoConfigs(ssoConfigs))
   .catch(() => {})

    async function fetchSsoConfigs() {
      const response = await axios.get<SsoConfig[]>(
        `/servlets/cgi/api/ssoconfig/repo/${repoInfo?.repo}/viewer/${repoInfo?.viewerPath}`,
        {headers: {'Accept': 'application/json'}}
      )

      if (!response.headers['content-type'].includes('application/json')) {
        return []
      }

      return response.data
    }

  }, [ repoInfo?.repo ])

  const doLogin: SubmitHandler<AppLoginCredentials> = async loginCredentials => {
    if (!repoInfo) return

    try {
      setErrorReason(null)

      const resp = await appLogin(loginCredentials, repoInfo.repo, repoInfo.repoUrl)
      if (resp.success) {
       
        props.history.replace(props.location.state || '/')

        // @ts-ignore
        if (window.CONNECTED_APPS_STATIC) {
          // Workaround:
          // React-router's history.replace does not always rerender
          // when <Route path="*" /> is used (static apps),
          // causing a blank page.
          // https://stackoverflow.com/a/61596862
          window.location.reload()
        }
      } else {
        setErrorReason(getLoginErrorMessage(resp.status))
      }
    } catch (e) {
      console.error(e)
      setErrorReason("Login error")
    }
  }

  if (!repoInfo) {
    return (
      <LoginPageLayout>
        Invalid repository information.
      </LoginPageLayout>
    )
  }

  const redirectTo = typeof props.location.state === 'string' ? props.location.state : undefined

  const loginErrorMessage = getLoginErrorMessage(appLoginStatus)

  return (
    <LoginPageLayout>
      <h1>Welcome</h1>
      <form onSubmit={handleSubmit(doLogin)}>
        <p className="mb-2">
          This app requires a login.{' '}
          {auth.status === 'authorized' && auth.user.role === 'superadmin' && (
            `Repository: ${repoInfo.repo}`
          )}
        </p>

        {loginErrorMessage && (
          <p className="text-red-600 font-bold my-2">
            {loginErrorMessage}
          </p>
        )}

        <SingleLoginView returnTo={redirectTo} />

        <label>
          Username
          <input type="username" required
                 className="font-normal"
                 {...register('username', { required: true })} />
        </label>

        <label>
          Password
          <input type="password" required
                 className="font-normal"
                 {...register('password', { required: true })} />
        </label>

        <LoginButtonBar disabled={formState.isSubmitting}
                        isLoggingIn={formState.isSubmitting}
                        error={errorReason} />

        {ssoConfigs.length > 0 && (
          <div className="flex-column gap-2 mt-3">
            {ssoConfigs.map(ssoConfig => (
              <SsoButton key={ssoConfig.uri} href={`/servlets/cgi/api/sso/authorize/${ssoConfig.ssoClientId}?ssoSuccessRedirectUrl=${redirectTo}`}>
                <img
                  src={ssoConfig.image}
                  alt="" />
                <span>Sign in with {ssoConfig.label}</span>
              </SsoButton>
            ))}
          </div>
        )}

      </form>
    </LoginPageLayout>
  )
}

function getRepoInfoFromSearchParameters() {
  const queryString = window.location.search
  const urlParams = new URLSearchParams(queryString)
  const id = urlParams.get('id')
  if (id == null) return null

  const parametersString = atob(decodeURIComponent(id))
  const usp = new URLSearchParams(parametersString)
  const repoUrl = usp.get('repoUrl')
  if (repoUrl == null) throw new Error(`no "repoUrl" found in ${parametersString}`)
  const repo = usp.get('repo')
  if (repo == null) throw new Error(`no "repo" found in ${parametersString}`)
  const viewerPath = usp.get('viewerPath')
  if (viewerPath == null) throw new Error(`no "viewerPath" found in ${parametersString}`)

  return { repo, repoUrl, viewerPath }
}

function getLoginErrorMessage(appLoginStatus: AppLoginStatus) {
  switch (appLoginStatus) {
    case "UNINITIALIZED": /* fallthrough */
    case "NO_AUTHORIZATION_NEEDED": /* fallthrough */
    case "AUTHORIZED": /* fallthrough */
    case "FETCHING":   /* fallthrough */
    case "NOT_LOGGED_IN":
      return null
    case "USER_NOT_PERMITTED":  /* fallthrough */
    case "REPO_NOT_PERMITTED":
      return "Your account does not have sufficient permissions for this application."
    case "BAD_CREDENTIALS":
      return "Incorrect username or password."
    case "PENDING_AUTHORIZATION":
      return "Your account has been created but is not yet authorized. Please wait for an administrator to grant access."
    case "WRONG_REPO":
      return "This account is not valid for this application."
    case "FETCH_ERROR":
      return "Unable to connect to the user system."
    default:
      return "There was an unexpected problem with logging in."
  }
}
